import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styles from "../../styles/login.module.css";

import {
  csCheckPhoneExists,
  csResetSendOtp,
  csResetConfirmOtp,
  csResetSetNewPassword,
} from "../../api/password.api";

export default function ResetPassword() {
  const nav = useNavigate();

  // step: "PHONE" -> "OTP" -> "NEWPASS"
  const [step, setStep] = useState("PHONE");

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  // cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const canSendOtp = useMemo(() => phone.trim().length >= 7 && cooldown <= 0 && !loading, [phone, cooldown, loading]);

  const sendOtp = async () => {
    if (!canSendOtp) return;
    setMsg("");
    setLoading(true);
    try {
      // 1) phone var/yox
      await csCheckPhoneExists(phone);

      // 2) otp göndər
      await csResetSendOtp(phone);

      setStep("OTP");
      setCooldown(60);
      setMsg("OTP kodu WhatsApp vasitəsilə göndərildi.");
    } catch (err) {
      setMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Telefon tapılmadı və ya OTP göndərilmədi."
      );
    } finally {
      setLoading(false);
    }
  };

  const onOtpChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const confirmOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6) return setMsg("6 rəqəmli kodu daxil edin.");

    setMsg("");
    setLoading(true);
    try {
      await csResetConfirmOtp(phone, code);
      setStep("NEWPASS");
      setMsg("");
    } catch (err) {
      setMsg(err?.response?.data?.message || err?.message || "OTP yanlışdır.");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    setMsg("");

    if (newPass.trim().length < 6) return setMsg("Şifrə minimum 6 simvol olmalıdır.");
    if (newPass !== newPass2) return setMsg("Şifrələr eyni deyil.");

    setLoading(true);
    try {
      await csResetSetNewPassword(phone, newPass);
      // uğurlu
      nav("/couriersub/login", { replace: true });
    } catch (err) {
      setMsg(err?.response?.data?.message || err?.message || "Şifrə dəyişdirilmədi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.brand}>MotoTaksi.az</div>
          <h1 className={styles.title}>Şifrəni unutdum</h1>
          <p className={styles.subtitle}>
            Telefonu daxil et → OTP ilə təsdiq et → yeni şifrəni yaz.
          </p>
        </div>

        <div className={styles.form}>
          {/* STEP 1: PHONE */}
          {step === "PHONE" && (
            <>
              <label className={styles.label}>Telefon</label>
              <input
                className={styles.input}
                placeholder="0551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                autoComplete="username"
              />

              <button className={styles.btn} type="button" onClick={sendOtp} disabled={!canSendOtp}>
                {loading ? "Göndərilir..." : "OTP göndər"}
              </button>
            </>
          )}

          {/* STEP 2: OTP */}
          {step === "OTP" && (
            <>
              <label className={styles.label}>Telefon</label>
              <input
                className={styles.input}
                value={phone}
                readOnly
              />

              <div className={styles.otpGrid}>
                {otp.map((v, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    className={styles.otpInput}
                    maxLength={1}
                    value={v}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKeyDown(i, e)}
                    inputMode="numeric"
                  />
                ))}
              </div>

              <button className={styles.btn} type="button" onClick={confirmOtp} disabled={loading}>
                {loading ? "Yoxlanılır..." : "OTP təsdiqlə"}
              </button>

              <button
                type="button"
                onClick={sendOtp}
                disabled={loading || cooldown > 0}
                style={{
                  marginTop: 8,
                  background: "transparent",
                  border: "none",
                  color: cooldown > 0 ? "#888" : "#06f",
                  fontWeight: 700,
                  cursor: loading || cooldown > 0 ? "not-allowed" : "pointer",
                }}
              >
                {cooldown > 0 ? `Yenidən göndər (${cooldown}s)` : "OTP-ni yenidən göndər"}
              </button>
            </>
          )}

          {/* STEP 3: NEW PASS */}
          {step === "NEWPASS" && (
            <>
              <label className={styles.label}>Yeni şifrə</label>
              <input
                className={styles.input}
                type="password"
                placeholder="Minimum 6 simvol"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                autoComplete="new-password"
              />

              <label className={styles.label}>Yeni şifrə (təkrar)</label>
              <input
                className={styles.input}
                type="password"
                placeholder="Təkrar daxil edin"
                value={newPass2}
                onChange={(e) => setNewPass2(e.target.value)}
                autoComplete="new-password"
              />

              <button className={styles.btn} type="button" onClick={changePassword} disabled={loading}>
                {loading ? "Dəyişdirilir..." : "Şifrəni dəyiş"}
              </button>
            </>
          )}

          {msg && <div className={styles.error}>{msg}</div>}

          <div className={styles.footer}>
            <Link className={styles.link} to="/couriersub/login">
              Giriş səhifəsinə qayıt
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

